/**
 * Download Section - Clean design with device mockups
 */

import { Chrome, Apple, Smartphone, Download as DownloadIcon, Star, Users, Globe, Bell } from 'lucide-react';
import { useWebsiteContentOptional } from '../../contexts/WebsiteContentContext';
import { useLanguage } from '../../contexts/LanguageContext';
import EditableElement from '../admin/EditableElement';

const stats = [
  { id: 1, icon: Users },
  { id: 2, icon: Star },
  { id: 3, icon: Globe },
  { id: 4, icon: Bell },
];

function PhoneMockupVisual({ t }: { t: (key: string, fallback: string) => string }) {
  return (
    <div className="relative w-48 h-80 mx-auto">
      {/* Phone frame */}
      <div className="absolute inset-0 bg-gray-900 rounded-[2.5rem] shadow-2xl">
        {/* Notch */}
        <div className="absolute top-2 left-1/2 -translate-x-1/2 w-20 h-6 bg-gray-900 rounded-full" />
        {/* Screen */}
        <div className="absolute top-4 left-2 right-2 bottom-4 bg-white rounded-[2rem] overflow-hidden">
          {/* App content */}
          <div className="p-3">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 bg-primary-600 rounded-lg" />
              <div className="h-3 bg-gray-200 rounded w-16" />
            </div>
            {/* Product cards */}
            <div className="space-y-2">
              <div className="p-2 bg-primary-50 rounded-lg border border-primary-100">
                <div className="flex gap-2">
                  <div className="w-10 h-10 bg-primary-100 rounded" />
                  <div className="flex-1">
                    <div className="h-2 bg-gray-200 rounded w-full mb-1" />
                    <div className="h-2 bg-primary-200 rounded w-12" />
                  </div>
                </div>
              </div>
              <div className="p-2 bg-gray-50 rounded-lg border border-gray-100">
                <div className="flex gap-2">
                  <div className="w-10 h-10 bg-gray-100 rounded" />
                  <div className="flex-1">
                    <div className="h-2 bg-gray-200 rounded w-full mb-1" />
                    <div className="h-2 bg-gray-200 rounded w-16" />
                  </div>
                </div>
              </div>
              <div className="p-2 bg-gray-50 rounded-lg border border-gray-100">
                <div className="flex gap-2">
                  <div className="w-10 h-10 bg-gray-100 rounded" />
                  <div className="flex-1">
                    <div className="h-2 bg-gray-200 rounded w-full mb-1" />
                    <div className="h-2 bg-gray-200 rounded w-14" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Floating notification */}
      <div className="absolute -right-4 top-20 bg-white rounded-xl p-3 shadow-xl animate-float border border-gray-100">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
            <Bell className="w-4 h-4 text-white" />
          </div>
          <div>
            <div className="text-xs font-semibold text-gray-900">{t('download.notification.backInStock', 'Back in stock!')}</div>
            <div className="text-[10px] text-green-600">{t('download.mockup.kallaxShelf', 'KALLAX shelf')}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function BrowserMockupVisual({ t }: { t: (key: string, fallback: string) => string }) {
  return (
    <div className="relative w-72 h-48 mx-auto">
      {/* Browser frame */}
      <div className="absolute inset-0 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
        {/* Browser bar */}
        <div className="flex items-center gap-1.5 px-3 py-2 bg-gray-100 border-b border-gray-200">
          <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
          <div className="flex-1 mx-2">
            <div className="h-5 bg-white rounded px-2 flex items-center">
              <span className="text-[10px] text-gray-400">{t('download.mockup.ikeaUrl', 'ikea.com/product/...')}</span>
            </div>
          </div>
        </div>
        {/* Extension popup */}
        <div className="p-3">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-6 h-6 bg-primary-600 rounded flex items-center justify-center">
              <span className="text-white text-xs font-bold">R</span>
            </div>
            <span className="text-xs font-semibold text-gray-900">{t('download.extension.name', 'RUMIO Extension')}</span>
          </div>
          <div className="bg-gray-50 rounded-lg p-2 mb-2">
            <div className="text-[10px] text-gray-500 mb-1">{t('download.mockup.malmBedFrame', 'MALM Bed frame')}</div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-green-600">$199</span>
              <span className="text-[10px] text-gray-400 line-through">$249</span>
              <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-[10px] font-semibold rounded">-20%</span>
            </div>
          </div>
          <button className="w-full py-1.5 bg-accent text-white text-xs font-semibold rounded-lg">
            {t('download.extension.trackProduct', 'Track Product')}
          </button>
        </div>
      </div>
      {/* Floating badge */}
      <div className="absolute -left-3 -top-2 px-2 py-1 bg-accent text-white text-[10px] font-bold rounded-full shadow-lg">
        ★ 4.8
      </div>
    </div>
  );
}

export default function Download() {
  const { t } = useLanguage();
  const content = useWebsiteContentOptional();

  const get = (key: string, fallback: string) =>
    content?.getContent('download', key, fallback) ?? fallback;

  const statLabels = [
    { key: 'stat1', valueDefault: '10K+', labelDefault: 'Downloads' },
    { key: 'stat2', valueDefault: '4.8', labelDefault: 'App Rating' },
    { key: 'stat3', valueDefault: '50+', labelDefault: 'Countries' },
    { key: 'stat4', valueDefault: '1M+', labelDefault: 'Alerts Sent' },
  ];

  return (
    <section id="download" className="py-20 relative overflow-hidden bg-primary-600">
      {/* Subtle background */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-10 left-10 w-40 h-40 bg-white rounded-full blur-3xl" />
        <div className="absolute bottom-10 right-10 w-60 h-60 bg-white rounded-full blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center max-w-2xl mx-auto mb-12">
          <EditableElement section="download" field="badge">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/20 rounded-full text-sm font-medium text-white mb-4">
              <DownloadIcon className="w-4 h-4" />
              {get('badge', 'Download')}
            </div>
          </EditableElement>
          <EditableElement section="download" field="title">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white">
              {get('title', 'Get RUMIO on')}
              <span className="text-accent">
                {' '}{get('title_highlight', 'all your devices')}
              </span>
            </h2>
          </EditableElement>
          <EditableElement section="download" field="subtitle">
            <p className="mt-4 text-lg text-primary-100">
              {get('subtitle', 'Track products on the go with our mobile app or while browsing with our browser extension.')}
            </p>
          </EditableElement>
        </div>

        {/* Device mockups and download buttons */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
          {/* Device mockups */}
          <div className="flex items-center justify-center gap-8">
            <div className="hidden sm:block">
              <PhoneMockupVisual t={t} />
            </div>
            <div className="transform translate-y-8">
              <BrowserMockupVisual t={t} />
            </div>
          </div>

          {/* Download buttons */}
          <div className="space-y-4">
            <EditableElement section="download" field="chrome_label">
              <a
                href={get('chrome_url', '#')}
                className="group flex items-center gap-4 p-4 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 hover:bg-white/20 transition-all duration-300 hover:-translate-y-1"
              >
                <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                  <Chrome className="w-8 h-8 text-gray-700" />
                </div>
                <div className="flex-1">
                  <div className="text-sm text-white/70">{get('chrome_prefix', 'Add to')}</div>
                  <div className="text-xl font-bold text-white">{get('chrome_label', 'Chrome')}</div>
                </div>
                <div className="px-3 py-1 bg-white/20 text-white text-sm font-semibold rounded-full">
                  {t('download.badge.free', 'Free')}
                </div>
              </a>
            </EditableElement>

            <EditableElement section="download" field="appstore_label">
              <a
                href={get('appstore_url', '#')}
                className="group flex items-center gap-4 p-4 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 hover:bg-white/20 transition-all duration-300 hover:-translate-y-1"
              >
                <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                  <Apple className="w-8 h-8 text-gray-700" />
                </div>
                <div className="flex-1">
                  <div className="text-sm text-white/70">{get('appstore_prefix', 'Download on the')}</div>
                  <div className="text-xl font-bold text-white">{get('appstore_label', 'App Store')}</div>
                </div>
                <div className="px-3 py-1 bg-white/20 text-white text-sm font-semibold rounded-full">
                  {t('download.badge.ios', 'iOS')}
                </div>
              </a>
            </EditableElement>

            <EditableElement section="download" field="playstore_label">
              <a
                href={get('playstore_url', '#')}
                className="group flex items-center gap-4 p-4 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 hover:bg-white/20 transition-all duration-300 hover:-translate-y-1"
              >
                <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                  <Smartphone className="w-8 h-8 text-gray-700" />
                </div>
                <div className="flex-1">
                  <div className="text-sm text-white/70">{get('playstore_prefix', 'Get it on')}</div>
                  <div className="text-xl font-bold text-white">{get('playstore_label', 'Google Play')}</div>
                </div>
                <div className="px-3 py-1 bg-white/20 text-white text-sm font-semibold rounded-full">
                  {t('download.badge.android', 'Android')}
                </div>
              </a>
            </EditableElement>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            const { key, valueDefault, labelDefault } = statLabels[index];

            return (
              <EditableElement key={stat.id} section="download" field={`${key}_value`}>
                <div className="text-center p-6 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 hover:bg-white/20 transition-all duration-300">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-3xl font-bold text-white mb-1">
                    {get(`${key}_value`, valueDefault)}
                  </div>
                  <div className="text-sm text-white/70">
                    {get(`${key}_label`, labelDefault)}
                  </div>
                </div>
              </EditableElement>
            );
          })}
        </div>
      </div>
    </section>
  );
}
